import { BarShapeProps, GroupedBarConfig } from "@Layouts/components/Charts/GroupedBarChart/types";

type TickResult = {
	ticks: number[];
	domainMax: number;
	step: number;
};

function formatCount(n: number): string {
	return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n);
}

/**
 * Build "nice" ticks from 0..domainMax given a max value and desired tick count.
 *
 * - tickCount is the EXACT target (we'll try very hard to match it).
 * - headroomRatio adds a small padding so the tallest bar doesn't kiss the top.
 * - nice steps are based on 1, 2, 2.5, 5, 10 × 10^k.
 */
function buildNiceTicks(maxValue: number, tickCount = 5, headroomRatio = 0.03): TickResult {
	const safeMax = Math.max(0, maxValue);

	// Degenerate cases
	if (!Number.isFinite(safeMax) || tickCount < 2) {
		return { ticks: [0], domainMax: 0, step: 0 };
	}

	if (safeMax === 0) {
		// If you want N ticks, give 0..(N-1)
		const ticks = Array.from({ length: tickCount }, (_, i) => i);
		return { ticks, domainMax: tickCount - 1, step: 1 };
	}

	const paddedMax = safeMax * (1 + headroomRatio);

	// Ideal step for exactly tickCount ticks from 0..domainMax
	const idealStep = paddedMax / (tickCount - 1);

	const candidates = niceStepCandidates(idealStep);

	let best: TickResult | null = null;
	let bestScore = Number.POSITIVE_INFINITY;

	for (const step of candidates) {
		if (step <= 0 || !Number.isFinite(step)) continue;

		const domainMax = ceilToStep(paddedMax, step);

		const ticks = buildTicksFromZero(domainMax, step);

		// Score: prioritize matching tick count; then prefer less wasted headroom
		const countDiff = Math.abs(ticks.length - tickCount);
		const headroomWaste = domainMax - paddedMax;

		// Weighted score: tick count mismatch dominates
		const score = countDiff * 1e6 + headroomWaste;

		if (score < bestScore) {
			bestScore = score;
			best = { ticks, domainMax, step };
		}
	}

	// Fallback (should be rare)
	if (!best) {
		const step = idealStep;
		const domainMax = ceilToStep(paddedMax, step);
		const ticks = buildTicksFromZero(domainMax, step);
		return { ticks, domainMax, step };
	}

	// If we got one-off due to floating error, nudge by trimming/expanding safely
	// (Usually unnecessary, but it makes the function more “bulletproof”.)
	best = forceTickCountIfClose(best, tickCount);

	return best;
}

/** Generate ticks: 0, step, 2*step, ... domainMax (inclusive). */
function buildTicksFromZero(domainMax: number, step: number): number[] {
	const n = Math.round(domainMax / step);
	const ticks: number[] = [];
	for (let i = 0; i <= n; i++) {
		ticks.push(i * step);
	}
	return ticks;
}

function ceilToStep(value: number, step: number): number {
	// Small epsilon to avoid e.g. ceil(2.00000000004/2) -> 2 steps too far
	const eps = step * 1e-12;
	return Math.ceil((value - eps) / step) * step;
}

/**
 * Produce a small set of "nice" step candidates around the ideal.
 * We take the base nice step, and also a few neighbors (half/double, etc.)
 * so we can hit the requested tick count more reliably.
 */
function niceStepCandidates(idealStep: number): number[] {
	const base = niceStep(idealStep);

	const candidates = new Set<number>([base, base / 2, base / 5, base * 2, base * 5]);

	// Also consider "one notch" down/up in the 1-2-2.5-5-10 ladder
	const ladderNeighbors = neighborNiceSteps(idealStep);
	for (const s of ladderNeighbors) candidates.add(s);

	// Return sorted small-to-large for determinism
	return [...candidates].filter((s) => s > 0 && Number.isFinite(s)).sort((a, b) => a - b);
}

/** Classic nice step: 1, 2, 2.5, 5, 10 × 10^k */
function niceStep(x: number): number {
	const exp = Math.floor(Math.log10(x));
	const f = x / Math.pow(10, exp);

	const nf = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10;

	return nf * Math.pow(10, exp);
}

/**
 * Returns a couple of adjacent nice steps around x (previous and next in the ladder)
 * to better approximate the target tickCount.
 */
function neighborNiceSteps(x: number): number[] {
	const exp = Math.floor(Math.log10(x));
	const f = x / Math.pow(10, exp);

	const ladder = [1, 2, 2.5, 5, 10];

	// Find insertion point
	let idx = ladder.findIndex((v) => f <= v);
	if (idx === -1) idx = ladder.length - 1;

	const steps: number[] = [];
	const cur = ladder[idx] * Math.pow(10, exp);
	steps.push(cur);

	if (idx > 0) steps.push(ladder[idx - 1] * Math.pow(10, exp));
	if (idx < ladder.length - 1) steps.push(ladder[idx + 1] * Math.pow(10, exp));

	// Also consider crossing exponent boundary (e.g., prev of 1×10^k is 10×10^(k-1))
	if (idx === 0) steps.push(10 * Math.pow(10, exp - 1));
	if (idx === ladder.length - 1) steps.push(Math.pow(10, exp + 1));

	return steps;
}

/**
 * If we are off by 1 tick due to rounding, fix it gently.
 * We do NOT do violent reshaping—only small, safe adjustments.
 */
function forceTickCountIfClose(res: TickResult, tickCount: number): TickResult {
	const diff = res.ticks.length - tickCount;

	if (Math.abs(diff) !== 1) return res;

	if (diff === 1) {
		// Too many ticks: drop the last tick (domainMax shrinks accordingly)
		const ticks = res.ticks.slice(0, -1);
		return { ...res, ticks, domainMax: ticks[ticks.length - 1] };
	} else {
		// Too few ticks: add one more tick
		const ticks = [...res.ticks, res.domainMax + res.step];
		return { ...res, ticks, domainMax: ticks[ticks.length - 1] };
	}
}

// Create a rounded rectangle path
function roundedTopRectPath(x: number, y: number, w: number, h: number, r: number): string {
	const rr = Math.max(0, Math.min(r, w / 2, h));
	return [
		`M ${x} ${y + h}`,
		`L ${x} ${y + rr}`,
		`Q ${x} ${y} ${x + rr} ${y}`,
		`L ${x + w - rr} ${y}`,
		`Q ${x + w} ${y} ${x + w} ${y + rr}`,
		`L ${x + w} ${y + h}`,
		"Z",
	].join(" ");
}

// Grouped bars component
function createGroupedBar<T>(bars: GroupedBarConfig<T>[], colors: string[], parentBarColor: string, totalKey?: keyof T) {
	return function GroupedBar({ x, y, width, height, payload }: BarShapeProps<T>) {
		if (x == null || y == null || width == null || height == null || !payload || width <= 0) {
			return null;
		}

		// Get values for all bars
		const barValues = bars.map((bar) => Math.max(0, (payload[bar.dataKey] as number) ?? 0));
		const totalValue = totalKey ? (payload[totalKey] as number) : barValues.reduce((sum, val) => sum + val, 0);

		if (!totalValue || totalValue <= 0) {
			return null;
		}

		// Use the actual height provided by Recharts - don't scale it
		const parentH = height;
		const parentY = y;

		// Calculate inner bar dimensions
		const PARENT_W_BASE = 72;
		const INNER_W_BASE = 16;
		const scaleX = width / PARENT_W_BASE;
		const innerW = INNER_W_BASE * scaleX;
		const gap = Math.max(0, (width - bars.length * innerW) / (bars.length + 1));

		// Calculate heights proportionally based on totalValue
		const denom = Math.max(1e-9, totalValue);
		const MIN_VISIBLE_PX = 2;

		const barHeights = barValues.map((val) => {
			if (val <= 0) {
				return 0;
			}
			const rawH = (val / denom) * parentH;
			return Math.max(MIN_VISIBLE_PX, rawH);
		});

		const parentR = Math.min(10 * scaleX, width / 2, parentH);
		const innerR = Math.min(4 * scaleX, innerW / 2);

		return (
			<g transform={`translate(${x}, ${parentY})`}>
				{/* Parent bar */}
				<path d={roundedTopRectPath(0, 0, width, parentH, parentR)} fill={parentBarColor} strokeWidth={0} />

				{/* Inner bars */}
				{barHeights.map((h, i) => {
					if (h <= 0) return null;
					const innerX = gap + i * (innerW + gap);
					const innerY = parentH - h;
					const color = bars[i].color || colors[i] || colors[0];

					return <path key={i} d={roundedTopRectPath(innerX, innerY, innerW, h, innerR)} fill={color} />;
				})}
			</g>
		);
	};
}
export { roundedTopRectPath, buildNiceTicks, formatCount, createGroupedBar };
